import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "./theme-toggle";
import type { ReactNode } from "react";
import { createContext, useContext } from "react";

export interface WorkshopTopbarContextValue {
  title: string;
  backTo: string;
  backLabel: string;
  version?: number;
  actions?: ReactNode;
}

const WorkshopTopbarContext = createContext<WorkshopTopbarContextValue | null>(
  null,
);

export function useWorkshopTopbar() {
  return useContext(WorkshopTopbarContext);
}

export { WorkshopTopbarContext };

export function WorkshopTopbar() {
  const ctx = useContext(WorkshopTopbarContext);
  const { id: projectId } = useParams<{ id: string }>();

  const backTo = ctx?.backTo ?? `/projects/${projectId ?? ""}`;
  const backLabel = ctx?.backLabel ?? "Project";
  const title = ctx?.title ?? "";

  return (
    <header className="flex h-11 shrink-0 items-center gap-3 border-b bg-background px-3">
      <Link
        to={backTo}
        className="flex shrink-0 max-w-[140px] sm:max-w-none items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 shrink-0" />
        <span className="truncate">{backLabel}</span>
      </Link>

      {title && (
        <>
          <span className="text-muted-foreground" aria-hidden="true">
            /
          </span>
          <span className="truncate text-sm font-medium">{title}</span>
        </>
      )}

      {ctx?.version != null && (
        <Badge variant="secondary" className="text-xs">
          v{ctx.version}
        </Badge>
      )}

      <div className="ml-auto flex items-center gap-1">
        {ctx?.actions}
        <ThemeToggle />
      </div>
    </header>
  );
}
